import { useQuery } from '@tanstack/react-query'
import { ImageOff, Search, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { sceneryCandidatesQuery, type SceneryCandidate } from '@/api/stages'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'

import { BAND_SHORT, clipLabel } from './words'

/** One page of things: every candidate the server has (it caps at 500). */
export const CANDIDATE_PAGE = 500

/**
 * "Add a thing": the rigged catalog things that can live in a background (trees, flowers, clouds,
 * birds, fish…), the ones that suit this background first (the server ranks by its world and sizes
 * each for its picture). Search by name; pick one and it is placed where it belongs (sky things up
 * high, ground things on this picture's ground), ready to drag.
 */

export interface AddSceneryDialogProps {
  open: boolean
  onOpenChange(open: boolean): void
  /** The background: its things come first, sized for its picture. */
  stageUid: string
  onPick(candidate: SceneryCandidate): void
}

export function AddSceneryDialog({ open, onOpenChange, stageUid, onPick }: AddSceneryDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" data-testid="add-scenery">
        <DialogHeader>
          <DialogTitle>Add something to the background</DialogTitle>
          <DialogDescription>Pick a tree, a cloud, a fish… It lands where it belongs — then drag it where you want it.</DialogDescription>
        </DialogHeader>
        {open ? (
          <Picker
            stageUid={stageUid}
            onPick={(c) => {
              onPick(c)
              onOpenChange(false)
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function Picker({ stageUid, onPick }: { stageUid: string; onPick(c: SceneryCandidate): void }) {
  const [draft, setDraft] = useState('')
  const [q, setQ] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setQ(draft.trim()), 250)
    return () => clearTimeout(t)
  }, [draft])

  const list = useQuery(sceneryCandidatesQuery({ stage_uid: stageUid, q, limit: CANDIDATE_PAGE }))
  const items = list.data ?? []

  return (
    <div className="flex min-h-0 flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
          <Input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Find: tree, cloud, flower, fish…" aria-label="Search things" className="pl-9 pr-9" />
          {draft ? (
            <button type="button" onClick={() => setDraft('')} aria-label="Clear search" className="absolute right-2 top-1/2 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-lg text-ink-3 hover:bg-paper-2 hover:text-ink">
              <X className="size-4" aria-hidden />
            </button>
          ) : null}
        </div>
        <p className="text-xs text-ink-3">Things that suit this place come first.</p>
      </div>

      <div className="max-h-[55vh] min-h-40 overflow-y-auto pr-1">
        {list.isPending ? (
          <p className="p-6 text-center text-sm text-ink-2">Looking…</p>
        ) : list.isError ? (
          <div className="flex flex-col items-center gap-2 p-6" role="alert">
            <p className="text-sm text-ink">The things could not be loaded.</p>
            <Button variant="secondary" size="sm" onClick={() => void list.refetch()}>
              Try again
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-6 text-center text-sm text-ink-2">
            <p>{q ? `Nothing called “${q}”.` : 'Nothing that can live in a background yet.'}</p>
            <p className="text-xs text-ink-3">Things become scenery once they can move — see Puppets.</p>
          </div>
        ) : (
          <>
            {items.length >= CANDIDATE_PAGE ? <p className="pb-2 text-xs text-ink-3">These are the first {CANDIDATE_PAGE} — search by name to find the rest.</p> : null}
            <ul className="grid grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-2" aria-label="Things" data-testid="candidate-grid">
              {items.map((c) => (
                <li key={c.puppet_uid}>
                  <button
                    type="button"
                    onClick={() => onPick(c)}
                    className="card flex w-full flex-col overflow-hidden text-left transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-pop focus-visible:focus-ring"
                    aria-label={`Add ${c.name}`}
                    data-testid="candidate"
                  >
                    <CandidateThumb src={c.thumb} />
                    <span className="truncate px-2 pt-1.5 text-sm font-semibold text-ink" title={c.name}>
                      {c.name}
                    </span>
                    <span className="truncate px-2 pb-2 text-xs text-ink-3">
                      {BAND_SHORT[c.band]} · {clipLabel(c.ambient)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}

function CandidateThumb({ src }: { src: string | null }) {
  const [broken, setBroken] = useState(false)
  return (
    <span className="flex aspect-square items-center justify-center bg-paper-2">
      {src && !broken ? (
        <img src={src} alt="" loading="lazy" draggable={false} onError={() => setBroken(true)} className="size-full object-contain p-2" />
      ) : (
        <ImageOff className="size-6 text-ink-3" aria-hidden />
      )}
    </span>
  )
}
